import {
    createContext,
    useContext,
    useEffect,
    useReducer,
    type ReactNode,
} from 'react';
import { authApi } from '@/api/auth';
import type { User } from '@/types/types';

const STORAGE_KEY = 'user';

const INITIAL_STATE: User = {
    email: '',
    idToken: '',
    refreshToken: '',
    expiresAt: 0,
    loggedIn: false,
    firstName: '',
    lastName: '',
};

const loadInitial = (): User => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return INITIAL_STATE;
        const parsed = JSON.parse(raw) as User;
        return { ...INITIAL_STATE, ...parsed };
    } catch {
        return INITIAL_STATE;
    }
};

type AuthAction =
    | { type: 'LOGIN'; payload: Partial<User> }
    | { type: 'LOGOUT' }
    | { type: 'REFRESH_TOKEN'; payload: Partial<User> }
    | { type: 'COMPLETE_LOGIN' }
    | { type: 'RESET_PASSWORD' };

const reducer = (state: User, action: AuthAction): User => {
    switch (action.type) {
        case 'LOGIN':
            return { ...state, ...action.payload, loggedIn: true };
        case 'LOGOUT':
            return { ...INITIAL_STATE };
        case 'REFRESH_TOKEN':
            return { ...state, ...action.payload };
        case 'COMPLETE_LOGIN':
            return { ...state, loggedIn: true };
        case 'RESET_PASSWORD':
            return state;
        default:
            return state;
    }
};

export interface AuthContextValue {
    state: User;
    firstName: string;
    lastName: string;
    actions: {
        login: (
            email: string,
            password: string,
            onSuccess?: (data: unknown) => void,
            onError?: (err: unknown) => void,
        ) => Promise<void>;
        logout: () => Promise<void>;
        resetPassword: (
            email: string,
            onSuccess?: (email: string) => void,
            onError?: (err: unknown, email: string) => void,
        ) => Promise<void>;
        completeLogin: () => void;
        refreshToken: () => Promise<string | null>;
    };
    DISPATCH: (action: AuthAction) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const persistAuthFields = (patch: Partial<User>) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const current = raw ? (JSON.parse(raw) as Partial<User>) : {};
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...current, ...patch }),
        );
    } catch {}
};

const clearPersistedAuth = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {}
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE, loadInitial);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    const value: AuthContextValue = {
        state,
        firstName: state.firstName ?? '',
        lastName: state.lastName ?? '',
        actions: {
            login: async (email, password, onSuccess, onError) => {
                try {
                    const res = await authApi.login(email, password);
                    if (res.status === 200 || res.status === 201) {
                        const body = res.data as unknown as {
                            payload?: {
                                user: string;
                                idToken: string;
                                refreshToken: string;
                                expiresAt: number;
                            };
                            data?: {
                                user: string;
                                idToken: string;
                                refreshToken: string;
                                expiresAt: number;
                            };
                        };
                        const data = body.payload ?? body.data;
                        if (!data) throw new Error('Unexpected login response shape');
                        const patch: Partial<User> = {
                            email: data.user,
                            idToken: data.idToken,
                            refreshToken: data.refreshToken,
                            expiresAt: data.expiresAt,
                            loggedIn: true,
                        };
                        persistAuthFields(patch);
                        dispatch({ type: 'LOGIN', payload: patch });
                        onSuccess?.(data);
                    }
                } catch (err) {
                    onError?.(err);
                }
            },
            logout: async () => {
                clearPersistedAuth();
                dispatch({ type: 'LOGOUT' });
            },
            resetPassword: async (email, onSuccess, onError) => {
                try {
                    const res = await authApi.resetPassword(email);
                    if (res.status === 200 || res.status === 201) {
                        dispatch({ type: 'RESET_PASSWORD' });
                        onSuccess?.(email);
                    }
                } catch (err) {
                    onError?.(err, email);
                }
            },
            completeLogin: () => dispatch({ type: 'COMPLETE_LOGIN' }),
            refreshToken: async () => {
                const currentRefresh =
                    state.refreshToken ||
                    (() => {
                        try {
                            const raw = localStorage.getItem(STORAGE_KEY);
                            if (!raw) return '';
                            return (JSON.parse(raw) as Partial<User>).refreshToken ?? '';
                        } catch {
                            return '';
                        }
                    })();
                if (!currentRefresh) return null;
                try {
                    const res = await authApi.refreshToken(currentRefresh);
                    if (res.status !== 200 && res.status !== 201) return null;
                    const body = res.data as unknown as {
                        payload?: {
                            idToken: string;
                            refreshToken: string;
                            expiresAt: number;
                        };
                        data?: {
                            idToken: string;
                            refreshToken: string;
                            expiresAt: number;
                        };
                    };
                    const data = body.data ?? body.payload;
                    if (!data?.idToken) return null;
                    const patch: Partial<User> = {
                        idToken: data.idToken,
                        refreshToken: data.refreshToken,
                        expiresAt: data.expiresAt,
                    };
                    persistAuthFields(patch);
                    dispatch({ type: 'REFRESH_TOKEN', payload: patch });
                    return data.idToken;
                } catch {
                    return null;
                }
            },
        },
        DISPATCH: dispatch,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
};
